import React from "react";
import PropTypes from "prop-types";
import { dateFormat } from "../../utils/functions";
import { Link } from "react-router-dom";
import { Cards } from "../Cards.tsx";

const UserProfileSummary = ({ user, detailsUrl, userType, ...props }) => {
  if (!user) {
    return <div>No user details available.</div>;
  }

  return (
    <div className="user-profile-details">
      {/* Header Section */}
      <Cards classes="mb-24 user-profile__header">
        <div className="user-profile__header-content">
          <div className="user-profile__title-section">
            <div className="user-profile__icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" fill="var(--primary)"/>
                <path d="M12 14C7.58172 14 4 17.5817 4 22H20C20 17.5817 16.4183 14 12 14Z" fill="var(--primary)"/>
              </svg>
            </div>
            <div>
              <h4 className="user-profile__title">Profile Summary</h4>
              <p className="user-profile__subtitle">
                {user.firstName} {user.lastName} • {detailsUrl === "/service-user/" ? "Service User" : "Team Member"}
              </p>
            </div>
          </div>
          <div className="user-profile__avatar">
            <div className="user-profile__avatar-circle">
              {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
            </div>
          </div>
        </div>
      </Cards>

      {/* Personal Information Section */}
      <Cards classes="mb-24 user-profile__personal">
        <div className="user-profile__section-header">
          <div className="user-profile__section-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 8L12 13L4 8V6L12 11L20 6V8Z" fill="var(--primary)"/>
            </svg>
          </div>
          <h6>Personal Information</h6>
        </div>
        <div className="user-profile__info-grid">
          {detailsUrl === "/service-user/" && user.preferredName && (
            <div className="user-profile__info-item">
              <label>Preferred Name</label>
              <p className="user-profile__info-value">{user.preferredName}</p>
            </div>
          )}
          <div className="user-profile__info-item">
            <label>Date of Birth</label>
            <p className="user-profile__info-value">{dateFormat(user?.dateOfBirth, "do MMM, yyyy") || "N/A"}</p>
          </div>
          <div className="user-profile__info-item">
            <label>Gender</label>
            <p className="user-profile__info-value">{user?.gender || "N/A"}</p>
          </div>
          <div className="user-profile__info-item">
            <label>Ethnicity</label>
            <p className="user-profile__info-value">{user?.ethnicity || "N/A"}</p>
          </div>
        </div>
      </Cards>

      {/* Contact Information Section */}
      <Cards classes="mb-24 user-profile__contact">
        <div className="user-profile__section-header">
          <div className="user-profile__section-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M6.62 10.79C8.06 13.62 10.38 15.94 13.21 17.38L15.41 15.18C15.69 14.9 16.08 14.82 16.43 14.93C17.55 15.3 18.75 15.5 20 15.5C20.55 15.5 21 15.95 21 16.5V20C21 20.55 20.55 21 20 21C10.61 21 3 13.39 3 4C3 3.45 3.45 3 4 3H7.5C8.05 3 8.5 3.45 8.5 4C8.5 5.25 8.7 6.45 9.07 7.57C9.18 7.92 9.1 8.31 8.82 8.59L6.62 10.79Z" fill="var(--primary)"/>
            </svg>
          </div>
          <h6>Contact Details</h6>
        </div>
        <div className="user-profile__info-grid">
          <div className="user-profile__info-item">
            <label>Phone Number</label>
            <p className="user-profile__info-value">{user?.telephoneNumber || "N/A"}</p>
          </div>
          <div className="user-profile__info-item">
            <label>Email Address</label>
            <p className="user-profile__info-value">{user?.emailAddress || "N/A"}</p>
          </div>
          <div className="user-profile__info-item">
            <label>Address</label>
            <p className="user-profile__info-value">{user?.address || "N/A"}</p>
          </div>
          <div className="user-profile__info-item">
            <label>Postal Code</label>
            <p className="user-profile__info-value">{user?.postalCode || "N/A"}</p>
          </div>
        </div>
      </Cards>

      {/* Administrative Information Section */}
      <Cards classes="mb-24 user-profile__administrative">
        <div className="user-profile__section-header">
          <div className="user-profile__section-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M12 2C6.48 2 2 6.48 2 12S6.48 22 12 22 22 17.52 22 12 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" fill="var(--primary)"/>
            </svg>
          </div>
          <h6>Administrative Details</h6>
        </div>
        <div className="user-profile__info-grid">
          <div className="user-profile__info-item">
            <label>Start Date</label>
            <p className="user-profile__info-value">{dateFormat(user?.startDate, "do MMM, yyyy") || "N/A"}</p>
          </div>
          <div className="user-profile__info-item">
            <label>Documents</label>
            <p className="user-profile__info-value user-profile__documents-count">
              {user.documents?.length || 0} files
            </p>
          </div>
        </div>
      </Cards>

      {/* Action Buttons Section */}
      <Cards classes="mb-24 user-profile__actions-card">
        <div className="user-profile__section-header">
          <div className="user-profile__section-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M12 2L13.09 8.26L22 9L13.09 9.74L12 16L10.91 9.74L2 9L10.91 8.26L12 2Z" fill="var(--success)"/>
            </svg>
          </div>
          <h6>Quick Actions</h6>
        </div>
        <div className="user-profile__actions">
          <Link to={detailsUrl + (user?.staffId ?? user?.id)} className="user-profile__action-link">
            <button className="user-profile__action-btn user-profile__action-btn--primary">
              <div className="user-profile__action-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5S21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5ZM12 17C9.24 17 7 14.76 7 12S9.24 7 12 7S17 9.24 17 12S14.76 17 12 17ZM12 9C10.34 9 9 10.34 9 12S10.34 15 12 15S15 13.66 15 12S13.66 9 12 9Z" fill="currentColor"/>
                </svg>
              </div>
              View Details
            </button>
          </Link>

          <Link
            to={`/documents/${user?.staffId ?? user?.id}?userType=${userType}`}
            className="user-profile__action-link"
          >
            <button className="user-profile__action-btn user-profile__action-btn--documents">
              <div className="user-profile__action-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2ZM18 20H6V4H13V9H18V20Z" fill="currentColor"/>
                </svg>
              </div>
              Documents ({user.documents?.length || 0})
            </button>
          </Link>

          {detailsUrl === "/service-user/" && (
            <>
              <Link to={"/user-medication/" + user?.id} className="user-profile__action-link">
                <button className="user-profile__action-btn user-profile__action-btn--medication">
                  <div className="user-profile__action-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M19.14,12.94C19.73,12.35 19.73,11.45 19.14,10.86L13.14,4.86C12.76,4.49 12.26,4.29 11.74,4.29H5C3.9,4.29 3,5.19 3,6.29V11.74C3,12.26 3.2,12.76 3.57,13.14L9.86,19.43C10.45,20.02 11.35,20.02 11.94,19.43L19.14,12.94ZM6.5,9.79C5.4,9.79 4.5,8.89 4.5,7.79S5.4,5.79 6.5,5.79 8.5,6.69 8.5,7.79 7.6,9.79 6.5,9.79Z" fill="currentColor"/>
                    </svg>
                  </div>
                  Medication
                </button>
              </Link>

              <Link to={"/prn/" + user?.id} className="user-profile__action-link">
                <button className="user-profile__action-btn user-profile__action-btn--prn">
                  <div className="user-profile__action-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M12 2L13.09 8.26L22 9L13.09 9.74L12 16L10.91 9.74L2 9L10.91 8.26L12 2Z" fill="currentColor"/>
                    </svg>
                  </div>
                  PRN
                </button>
              </Link>

              <Link to={detailsUrl + (user?.staffId ?? user?.id)} className="user-profile__action-link">
                <button className="user-profile__action-btn user-profile__action-btn--care">
                  <div className="user-profile__action-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19ZM17 12H12V17H10V12H7V10H10V7H12V10H17V12Z" fill="currentColor"/>
                    </svg>
                  </div>
                  Care Plan
                </button>
              </Link>
            </>
          )}

          {detailsUrl === "/team-member/" && (
            <button
              className="user-profile__action-btn user-profile__action-btn--edit"
              onClick={props.editStaff}
            >
              <div className="user-profile__action-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M3 17.25V21H6.75L17.81 9.94L14.06 6.19L3 17.25ZM20.71 7.04C21.1 6.65 21.1 6.02 20.71 5.63L18.37 3.29C17.98 2.9 17.35 2.9 16.96 3.29L15.13 5.12L18.88 8.87L20.71 7.04Z" fill="currentColor"/>
                </svg>
              </div>
              Edit Staff
            </button>
          )}
        </div>
      </Cards>
    </div>
  );
};

UserProfileSummary.propTypes = {
  user: PropTypes.shape({
    preferredName: PropTypes.string,
    ethnicity: PropTypes.string,
    dateOfBirth: PropTypes.string,
    address: PropTypes.string,
    gender: PropTypes.string,
    emailAddress: PropTypes.string,
    telephoneNumber: PropTypes.string,
  }),
};

export default UserProfileSummary;
